import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { moFamilyStore } from '../stores/moFamilyStore';
import { SlotDisplay } from './SlotDisplay';
import { FamilyAvatar } from './FamilyAvatar';
import { formatWon } from '../utils/number';

interface HierarchyNodeData {
 familyHash: string;
 familyName: string;
 relationType: string;
 parentFamilyHash?: string;
 totalSlots: number;
 dedicatedSlots: number[];
 sharedSlots: number[];
 sharingType?: 'dedicated' | 'shared';
 sharedSlotsCount?: number;
 pricePerSlot?: number;
 ownerHash?: string;
 ownerName?: string;
 ownerProfileImage?: string | null;
 children?: HierarchyNodeData[];
}

interface HierarchyDisplayProps {
 hierarchyData: any[];
 currentFamilyHash?: string;
 selectedFamily?: any;
 userOwnedFamilies?: any[];
 onSlotDragStart?: () => void;
 onSlotDragEnd?: () => void;
 onFamilyVideosChanged?: () => void;
 /** 상위 모패밀리 — 있으면 트리 루트로 올려 상위→현재→하위를 한 트리로 연결한다. */
 parentFamily?: any;
}

export const HierarchyDisplay: React.FC<HierarchyDisplayProps> = ({
 hierarchyData,
 currentFamilyHash,
 parentFamily,
 selectedFamily,
 userOwnedFamilies = [],
 onSlotDragStart,
 onSlotDragEnd,
 onFamilyVideosChanged
}) => {
 const navigate = useNavigate();
 // 상위 목록 — 서버가 parents 배열을 주면 그걸, 아니면(구 응답) 단일 객체를 1개짜리로.
 // 1개: 종전대로 트리 루트로 승격(라인 연결). 여러 개: 루트 승격이 불가능(트리는 루트
 // 하나)하므로 트리 위에 칩으로 나열한다 — 다중 바터에서 슬롯별 상위가 다 다르다.
 const parentList: any[] = useMemo(() => {
  if (!parentFamily) return [];
  return Array.isArray(parentFamily.parents) && parentFamily.parents.length > 0
   ? parentFamily.parents
   : [parentFamily];
 }, [parentFamily]);
 const [familySlots, setFamilySlots] = useState<any[]>([]);
 const [editingSlotSet, setEditingSlotSet] = useState<string>('A');
 // 손자(belowCurrentDepth>=2) 이하 서브트리는 기본 접힘 — 여기 담긴 해시는 사용자가 펼친 노드.
 const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
 const toggleExpanded = (hash: string) => setExpandedNodes(prev => {
  const next = new Set(prev);
  if (next.has(hash)) next.delete(hash); else next.add(hash);
  return next;
 });
 
 useEffect(() => {
 if (currentFamilyHash) {
 const store = moFamilyStore.getState();
 setFamilySlots(store.getFamilySlots(currentFamilyHash));
 setEditingSlotSet(store.editingSlotSet || 'A');
 
 const unsubscribe = moFamilyStore.subscribe((state) => {
 setFamilySlots(state.getFamilySlots(currentFamilyHash));
 setEditingSlotSet(state.editingSlotSet || 'A');
 });
 
 return () => unsubscribe();
 }
 }, [currentFamilyHash]);
 
 
 const rootNode = useMemo(() => {
 // Always create a root node for the current family
 if (!currentFamilyHash) {
 return null;
 }
 
 // Create a map for quick lookup
 const familyMap = new Map<string, HierarchyNodeData>();
 
 // Always add the current family as the root
 familyMap.set(currentFamilyHash, {
 familyHash: currentFamilyHash,
 familyName: selectedFamily?.name || 'Current Family',
 relationType: 'self',
 totalSlots: selectedFamily?.totalSlots || 0,
 dedicatedSlots: [],
 sharedSlots: [],
 ownerHash: selectedFamily?.ownerMoPeerHash,
 ownerName: selectedFamily?.ownerName,
 ownerProfileImage: selectedFamily?.ownerProfileImage,
 children: []
 });
 
 // Add relationships from hierarchyData
 if (hierarchyData && hierarchyData.length > 0) {
 hierarchyData.forEach(item => {
 // Don't overwrite the current family node
 if (!familyMap.has(item.familyHash)) {
 familyMap.set(item.familyHash, { ...item, children: [] });
 } else if (item.familyHash === currentFamilyHash) {
 // Update current family with hierarchy data but keep it as root
 const currentNode = familyMap.get(currentFamilyHash)!;
 currentNode.totalSlots = item.totalSlots || currentNode.totalSlots;
 currentNode.dedicatedSlots = item.dedicatedSlots || [];
 currentNode.sharedSlots = item.sharedSlots || [];
 }
 });
 }
 
 // Build parent-child relationships
 if (hierarchyData && hierarchyData.length > 0) {
 hierarchyData.forEach(item => {
 if (item.relationType === 'child' && item.parentFamilyHash) {
 const parent = familyMap.get(item.parentFamilyHash);
 const child = familyMap.get(item.familyHash);
 if (parent && child) {
 parent.children!.push(child);
 }
 }
 });
 }
 
 const currentRoot = familyMap.get(currentFamilyHash || '');

 // 상위 모패밀리가 **정확히 1개**면 그걸 트리 루트로 올리고 현재 family 를 자식으로 —
 // 상위→현재→하위가 한 트리(라인+점)로 연결돼 보인다. 여러 개면 승격하지 않고
 // (트리는 루트가 하나) 렌더 단계의 상위 칩 나열이 맡는다.
 if (parentList.length === 1 && currentRoot) {
 const p = parentList[0];
 return {
 familyHash: p.moFamilyHash,
 familyName: p.name,
 relationType: 'parent',
 totalSlots: p.totalSlots || 0,
 dedicatedSlots: [],
 sharedSlots: [],
 ownerHash: p.ownerMoPeerHash,
 ownerName: p.ownerName,
 ownerProfileImage: p.ownerProfileImage,
 sharingType: p.sharingType,
 children: [currentRoot],
 } as HierarchyNodeData;
 }

 return currentRoot;
 }, [hierarchyData, currentFamilyHash, selectedFamily, parentList]);
 
 
 const renderNode = (node: HierarchyNodeData, depth: number = 0, parentNode: HierarchyNodeData | null = null, topLevelAuthorizedFamilyHash?: string, isLast: boolean = false, belowCurrentDepth: number = 0): React.JSX.Element | null => {
 if (!node) return null;
 
 const isCurrentFamily = node.familyHash === currentFamilyHash;
 // 상위(루트) 모패밀리 — 내가 수정할 수 없으므로 사선 패턴 배경으로 표시.
 const isParent = node.relationType === 'parent';
 const hasChildren = node.children && node.children.length > 0;
 
 // Check if current user owns this family
 // First check userOwnedFamilies array, then check selectedFamily if it matches
 const isUserOwnedFamily = userOwnedFamilies.some(f => f.moFamilyHash === node.familyHash) ||
 (isCurrentFamily && (selectedFamily?.role === 'owner' || selectedFamily?.role === 'operator' || selectedFamily?.isOwner));
 
 // 각 모패밀리에 대한 권한 확인
 let canDrop = false;
 if (isCurrentFamily) {
 // 현재 선택된 모패밀리인 경우
 canDrop = selectedFamily?.isOwner || selectedFamily?.role === 'owner' || selectedFamily?.role === 'operator';
 } else {
 // 하위 모패밀리인 경우
 // SlotDisplay 컴포넌트에서 개별 슬롯 권한을 확인하므로 여기서는 false로 설정
 canDrop = false;
 }
 const canManageRelationships = selectedFamily?.isOwner || selectedFamily?.role === 'owner' || selectedFamily?.role === 'operator';
 
 // Find user's family hash that has authority on slots
 // Calculate only once at the root level and pass down to children
 let userAuthorizedFamilyHash: string | undefined;
 
 if (depth === 0) {
 // 우선순위: 사용자가 '연'(선택한) 패밀리를 소유/운영하면 그것을 권한 기준으로 삼는다.
 // findUserFamily 는 트리에서 유저 소유 '첫' 패밀리를 순서대로 고르는데, 유저가 여러 패밀리를
 // 소유하고 대상 패밀리가 부모를 여럿 가지면(예: jg Lee → 서울킹 / jg Lee 바터 / …) 서울킹이
 // 아닌 엉뚱한 패밀리를 골라, 서울킹에 위임된 하위 슬롯이 잠기던 버그가 있었다. 열어본 패밀리
 // (currentFamilyHash)로 고정하면 "그 패밀리로서 보기" 가 일관된다.
 const currentIsOwned = !!currentFamilyHash && (
 userOwnedFamilies.some((uf: any) => uf.moFamilyHash === currentFamilyHash) ||
 (selectedFamily?.moFamilyHash === currentFamilyHash &&
 (selectedFamily?.isOwner || selectedFamily?.role === 'owner' || selectedFamily?.role === 'operator'))
 );
 // At root level, determine the authorized family
 if (currentIsOwned) {
 userAuthorizedFamilyHash = currentFamilyHash;
 } else if (isUserOwnedFamily) {
 // This is a family owned by the user - user has authority
 userAuthorizedFamilyHash = node.familyHash;
 } else {
 // Viewing another family - find user's family in the hierarchy
 const findUserFamily = (nodes: any[]): string | undefined => {
 for (const node of nodes) {
 // Check both in userOwnedFamilies and if it's the selectedFamily with owner/operator role
 const isOwned = userOwnedFamilies.some(uf => uf.moFamilyHash === node.familyHash) ||
 (node.familyHash === selectedFamily?.moFamilyHash && 
 (selectedFamily?.role === 'owner' || selectedFamily?.role === 'operator'));
 
 if (isOwned) {
 return node.familyHash;
 }
 if (node.children && node.children.length > 0) {
 const found = findUserFamily(node.children);
 if (found) return found;
 }
 }
 return undefined;
 };
 
 // First check in hierarchyData
 userAuthorizedFamilyHash = findUserFamily(hierarchyData);
 
 // If not found in hierarchy, check if current node is a user's family
 if (!userAuthorizedFamilyHash && userOwnedFamilies.some(uf => uf.moFamilyHash === node.familyHash)) {
 userAuthorizedFamilyHash = node.familyHash;
 }
 }
 } else {
 // Use the authorized family hash passed from parent
 userAuthorizedFamilyHash = topLevelAuthorizedFamilyHash;
 }
 
 // 직접적인 관계인지 확인
 // 1. 현재 패밀리가 부모이고 해당 노드가 자식인 경우
 const isDirectChild = parentNode?.familyHash === currentFamilyHash;
 // 2. 현재 패밀리가 자식이고 해당 노드가 부모인 경우
 // hierarchyData에서 현재 패밀리의 부모가 해당 노드인지 확인
 const isDirectParent = !isCurrentFamily && (
 node.relationType === 'parent' ||  // 합성된 상위 루트 노드
 hierarchyData?.some(data =>
 data.familyHash === currentFamilyHash &&
 data.relationType === 'child' &&
 data.parentFamilyHash === node.familyHash
 )
 );

 // 현재 패밀리로부터의 깊이: 0=현재/상위, 1=직속 하위, 2+=하위의 하위(손자 이하).
 // 손자 이하는 직속 하위를 통해서만 제어되고 현재 패밀리에 "직접" 노출되지는 않으므로,
 // 패밀리 테두리를 점선으로 해서 '간접(중간 하위 경유)'임을 표시한다.
 const isGrandDescendant = belowCurrentDepth >= 2;
 const childBelowCurrentDepth = isCurrentFamily ? 1 : (belowCurrentDepth > 0 ? belowCurrentDepth + 1 : 0);

 // 현재 패밀리에서 내가 권한 가진 슬롯이 하나도 없는지 — 슬롯 전부가 상위로 위임돼
 // 내가 직접 채울 슬롯이 없는 경우(예: 바터 패밀리). 원칙: 사선 = 내가 제어할 수 없음.
 // SlotDisplay 와 동일한 세트 선택 규칙(편집 중 세트 → active)으로 판정.
 const currentFamilyHasNoAuthority = (() => {
   if (!isCurrentFamily) return false;
   const sets = moFamilyStore.getState().slotSets.get(node.familyHash);
   if (!sets || sets.length === 0) return false; // 슬롯 정보 미로딩 — 판단 보류
   const setName = canDrop ? (editingSlotSet || 'A') : 'active';
   const targetSet = setName === 'active'
     ? (sets.find(s => s.isActive) || sets[0])
     : (sets.find(s => s.setName === setName) || sets.find(s => s.isActive) || sets[0]);
   const slots = Array.isArray(targetSet?.slots) ? targetSet.slots : [];
   if (slots.length === 0) return false; // 슬롯 0개면 사선 의미 없음
   return !slots.some((s: any) => Array.isArray(s.authorized_families) && s.authorized_families.includes(node.familyHash));
 })();
 // 슬롯 단위 'slot-no-authority' 와 같은 블루그레이 톤의 사선 패턴(제어 불가 일관 표시).
 const noAuthorityDiagonal = 'repeating-linear-gradient(45deg, rgba(96,125,139,0.14) 0, rgba(96,125,139,0.14) 5px, transparent 5px, transparent 12px)';

 // 접기 대상 노드(손자 이하 서브트리 보유) — 분기점 점 대신 클래식 트리 스타일 +/− 박스.
 const isCollapsible = !!hasChildren && childBelowCurrentDepth >= 2;
 const isNodeExpanded = expandedNodes.has(node.familyHash);

 return (
 <div
  key={node.familyHash}
  className={depth > 0
   ? `relative before:content-[''] before:absolute before:bg-gray-300 before:h-0.5 before:top-[1.1rem] md:before:top-[1.85rem] before:-left-2 before:w-[0.6rem] md:before:-left-4 md:before:w-[1.12rem]${
      isCollapsible ? '' : " after:content-[''] after:absolute after:rounded-full after:bg-gray-400 after:w-2 after:h-2 after:top-[0.91rem] md:after:top-[1.66rem] after:-left-3 md:after:-left-5"}`
   : undefined}
 >
 {/* depth>0 자식: 세로 레일(span) + 가로 분기선(before) + 분기점 점(after).
     마지막 자식은 레일을 분기점까지만 → └ 형태로 종료. */}
 {depth > 0 && (
 <span
  aria-hidden="true"
  className={`absolute -top-2 -left-2 md:-left-4 w-0.5 bg-gray-300 ${isLast ? 'h-[1.66rem] md:h-[2.41rem]' : 'bottom-0'}`}
 />
 )}
 {/* 클래식 파일트리 스타일 +/− 박스 — 분기점 점 자리에 겹쳐, 이 노드의 서브트리를 토글. */}
 {isCollapsible && depth > 0 && (
 <button
  onClick={() => toggleExpanded(node.familyHash)}
  title={isNodeExpanded ? '하위 접기' : `하위 모패밀리 ${node.children!.length}개 펼치기`}
  className="absolute z-10 w-3.5 h-3.5 -left-[0.94rem] top-[0.72rem] md:-left-[1.44rem] md:top-[1.47rem] flex items-center justify-center border border-gray-400 bg-white text-gray-600 text-[10px] leading-none font-mono hover:bg-gray-100"
 >{isNodeExpanded ? '−' : '+'}</button>
 )}
 <div>
 <div className={`px-1 py-2 md:p-4 ${
 isCurrentFamily
 ? (currentFamilyHasNoAuthority ? 'border border-gray-300' : (canManageRelationships ? '' : 'bg-gray-50 border-2 border-gray-400'))
 : (isGrandDescendant ? 'bg-white border border-dashed border-gray-400' : 'bg-white border border-gray-200')
 }`}
 style={isCurrentFamily && currentFamilyHasNoAuthority ? {
 backgroundImage: noAuthorityDiagonal
 } : isCurrentFamily && canManageRelationships ? {
 backgroundColor: 'rgba(26, 143, 203, 0.1)',
 border: '1px solid #1a8fcb'
 } : isParent ? {
 backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.045) 0, rgba(0,0,0,0.045) 5px, transparent 5px, transparent 12px)'
 } : {}}
 title={isCurrentFamily && currentFamilyHasNoAuthority ? '이 모패밀리에는 내가 제어할 수 있는 슬롯이 없습니다' : isParent ? '상위 모패밀리 — 내가 수정할 수 없습니다' : undefined}>
 <div className="flex items-start justify-between mb-2 gap-2">
 <div className="flex-1 min-w-0">
 <h4 className="font-medium text-sm md:text-lg flex items-center gap-1 md:gap-2 flex-wrap">
 {node.ownerName && (
 <FamilyAvatar
  name={node.ownerName}
  familyHash={node.ownerHash || node.ownerName}
  imageUrl={node.ownerProfileImage}
  size={20}
 />
 )}
 {isCurrentFamily ? (
 <>
 <span>{node.familyName}</span>
 {!canManageRelationships && (
 <span className="text-xs text-gray-600 font-normal">(권한 없음)</span>
 )}
 {editingSlotSet && canDrop && (
 <span className="text-xs text-purple-700 font-normal">슬롯 세트 {editingSlotSet} 편집 중</span>
 )}
 </>
 ) : (
 <button
 onClick={() => navigate(`/mofamily/${node.familyHash}`)}
 className="text-left cursor-pointer"
 >
 {node.familyName}
 </button>
 )}
 {node.sharingType && (
 <span className={`text-xs px-1.5 py-0.5 font-normal ${node.sharingType === 'dedicated' ? 'bg-blue-100 text-blue-800 border border-blue-200' : 'bg-green-100 text-green-800 border border-green-200'}`}>
 {node.sharingType === 'dedicated' ? '독점 위임' : '공유 위임'}
 </span>
 )}
 </h4>
 {node.sharingType && ((node.sharedSlotsCount ?? 0) > 0 || (node.pricePerSlot ?? 0) > 0) ? (
 <div className="text-sm text-gray-600 mt-1">
 {[
  (node.sharedSlotsCount ?? 0) > 0 ? `${node.sharedSlotsCount}개 슬롯` : null,
  (node.pricePerSlot ?? 0) > 0 ? `일일 ${formatWon(node.pricePerSlot)}` : null,
 ].filter(Boolean).join(' • ')}
 </div>
 ) : null}
 </div>
 {!isCurrentFamily && (
 <div className="flex gap-1 md:gap-2 flex-shrink-0">
 <button
 onClick={() => navigate(`/mofamily/${node.familyHash}`)}
 className="text-blue-600 hover:text-blue-800 text-xs md:text-sm px-2 md:px-3 py-0.5 md:py-1 border border-blue-600 hover:bg-blue-50"
 >
 상세보기
 </button>
 {/* Show end collaboration button only for direct parent-child relationships */}
 {canManageRelationships && (isDirectChild || isDirectParent) && (
 <button
 onClick={async () => {
 if (confirm(`${node.familyName}과의 협업을 종료하시겠습니까?`)) {
 try {
 const response = await fetch(`/mofamily/${currentFamilyHash}/relationships/${node.familyHash}`, {
 method: 'DELETE',
 credentials: 'include'
 });
 
 if (response.ok) {
 // Emit event to reload hierarchy
 const event = new CustomEvent('hierarchyUpdate', { 
 detail: { familyHash: currentFamilyHash } 
 });
 window.dispatchEvent(event);
 
 // Show success notification
 const notification = document.createElement('div');
 notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 shadow-lg z-50';
 notification.textContent = '협업이 종료되었습니다.';
 document.body.appendChild(notification);
 setTimeout(() => notification.remove(), 3000);
 } else {
 const error = await response.json();
 alert(error.error || '협업 종료에 실패했습니다.');
 }
 } catch (error) {
 console.error('Error ending collaboration:', error);
 alert('협업 종료 중 오류가 발생했습니다.');
 }
 }
 }}
 className="text-red-600 hover:text-red-800 text-xs md:text-sm px-2 md:px-3 py-0.5 md:py-1 border border-red-600 hover:bg-red-50"
 >
 협업 종료
 </button>
 )}
 </div>
 )}
 </div>
 
 {/* Use React SlotDisplay component */}
 <div className="flex justify-end">
 <SlotDisplay
 key={`${node.familyHash}-${userAuthorizedFamilyHash || 'none'}-${canDrop}`}
 familyHash={node.familyHash}
 dropEnabled={canDrop}
 onDragStart={onSlotDragStart}
 onDragEnd={onSlotDragEnd}
 currentTopFamilyHash={userAuthorizedFamilyHash}
 onFamilyVideosChanged={onFamilyVideosChanged}
 />
 </div>
 </div>
 </div>
 
 {/* Render children — 세로 레일은 자식별 span 이 그림(마지막 자식만 분기점까지 → └) */}
 {/* 손자 이상(childBelowCurrentDepth>=2)은 기본 접힘 — 선택 모패밀리와 직계 자식까지만
     기본 노출(2026-08-26). 토글은 분기점의 +/− 박스(클래식 트리 스타일). */}
 {hasChildren && (!isCollapsible || isNodeExpanded) && (
 <div className="ml-2 md:ml-4 pl-2 md:pl-4 mt-2 space-y-2">
 {node.children!.map((child, idx) =>
  renderNode(child, depth + 1, node, userAuthorizedFamilyHash, idx === node.children!.length - 1, childBelowCurrentDepth)
 )}
 </div>
 )}
 </div>
 );
 };
 
 if (!rootNode) {
 return null;
 }
 
 return (
 <div className="space-y-2">
 {/* 상위가 여러 개(다중 바터)면 칩으로 나열 — 각 칩에 어느 슬롯이 위임됐는지 표시.
     사선 배경 = 상위 노드와 같은 '내가 제어할 수 없음' 시각 규약. */}
 {parentList.length > 1 && (
 <div className="flex flex-wrap items-center gap-1.5 pb-1">
 <span className="text-xs text-gray-500 mr-1">상위 {parentList.length}개:</span>
 {parentList.map((p: any) => {
 const ded: number[] = p.dedicatedSlots || [];
 const shr: number[] = p.sharedSlots || [];
 const slotDesc = [
 ded.length > 0 ? `슬롯 ${ded.join(',')} 독점` : '',
 shr.length > 0 ? `슬롯 ${shr.join(',')} 공유` : '',
 ].filter(Boolean).join(' · ');
 return (
 <span
 key={p.moFamilyHash}
 className="inline-flex items-center gap-1 px-2 py-0.5 border border-gray-300 bg-gray-50 text-xs text-gray-700 slot-no-authority"
 title={`${p.name}${p.ownerName ? ` (${p.ownerName})` : ''}${slotDesc ? ` — ${slotDesc} 위임` : ''}`}
 >
 <span className="font-medium">{p.name}</span>
 {slotDesc && <span className="text-gray-500">{slotDesc}</span>}
 </span>
 );
 })}
 </div>
 )}
 {renderNode(rootNode, 0)}
 </div>
 );
};